import { NextPage } from 'next';
import { getUser } from 'common/api/user';
import type { UserState } from 'types/user';
import ErrorBoundary from 'components/ErrorBoundary';
import config from 'config';
import ErrorPage from 'containers/ErrorPage';
import TravelZone, { type TravelZoneResponse } from 'containers/TravelZone';
import { getOrCreateStore, type ReduxInitialProps } from 'stores';
import type { WavetrakContext } from 'types/wavetrak';

interface Props extends ReduxInitialProps {
  page: {
    travelZone: TravelZoneResponse;
    payloadStatus: number;
  };
  user: UserState | null;
}

const TravelZonePage: NextPage<Props> = (props) => {
  const {
    page: { payloadStatus },
  } = props;

  const pageContent = () => {
    if (payloadStatus !== 200) {
      return <ErrorPage statusCode={payloadStatus} />;
    }
    return <TravelZone {...props} />;
  };

  return (
    <ErrorBoundary error={payloadStatus === 500} errorBoundaryId="travel-zone-page">
      {pageContent()}
    </ErrorBoundary>
  );
};

TravelZonePage.getInitialProps = async (context: WavetrakContext) => {
  const { id } = context.query;
  const isServer = !!context.req;
  const user = await getUser(context, isServer);
  const { getState } = getOrCreateStore();

  const url = `${config.servicesURL}/travel/zones?id=${id}`;
  const result = await fetch(url);
  const { status } = await result.json();
  const { statusCode, travelZone } = status;

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      id,
    });
    return {
      enableTheme: true,
      enableCssBaseline: true,
      isFavoritesBarVisible: false,
      librariesToLoad: {
        jwplayer: true,
      },
      ssrReduxState: getState(),
      page: {
        payloadStatus: statusCode,
        travelZone,
      },
      user,
    };
  }

  return {
    enableTheme: true,
    enableCssBaseline: true,
    isFavoritesBarVisible: false,
    librariesToLoad: {
      jwplayer: true,
    },
    page: {
      payloadStatus: statusCode,
      travelZone,
    },
    user,
  };
};

export default TravelZonePage;
